<template>
  <DrawerModal :is-open="isOpen" title="Blocked players" @close="emit('close')" @submit="emit('close')">
    <p v-if="blockedUsers.length" class="info-blocked">
      List of blocked players, with whom you can no longer see each other's profiles and Gambits. You can unblock the
      player by tapping "x".
    </p>
    <div class="current-blocked">
      <p>Currently blocked players:</p>
    </div>
    <div class="blocked-users-list">
      <div v-for="user in blockedUsers" :key="user.id" class="blocked-user-item">
        <span class="name">
          {{ displayName(user) }}
        </span>
        <div class="rating">
          <gam-chip
            v-if="user.rating"
            v-bind="{
              leftIcon: GamIconName.GAMBIT,
              label: user.ratingDisplayMode === RatingDisplayMode.DOTS ? undefined : user.rating?.toString(),
              dotsRating: user.ratingDisplayMode === RatingDisplayMode.DOTS ? user.rating : undefined,
            }"
          />

          <GamButton
            label="X"
            :size="GamButtonSize.X_SMALL"
            :variant="GamButtonVariant.TERTIARY"
            @click="unBlockUser(user.id, displayName(user))"
            class="icon"
          />
        </div>
      </div>
      <div v-if="!blockedUsers.length" class="no-users">Players you block will appear here.</div>
    </div>
  </DrawerModal>
</template>

<script setup lang="ts">
import type { PersonalUserDto } from '@/core/data/user/user.interface';
import { RatingDisplayMode } from '@/core/data/user/user.type';
import { gambits, localize } from '@/core/gambits';
import { useAuthStore } from '@/stores/auth.store';
import DrawerModal from '@/views/components/DrawerModal.vue';
import GamButton from '@/views/components/GamButton.vue';
import GamChip from '@/views/components/GamChip.vue';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { storeToRefs } from 'pinia';
import { ref } from 'vue';

interface BlockedUsersSectionProps {
  isOpen: boolean;
}

defineProps<BlockedUsersSectionProps>();
const emit = defineEmits<{ close: [] }>();

const authStore = useAuthStore();
const { userDetails } = storeToRefs(authStore);
const blockedUsers = ref(userDetails.value?.blocked || []);

// A blocked player who deleted their account has no handle left to show, and the
// one they had may since have been taken by somebody else.
const displayName = (user: PersonalUserDto): string =>
  user.isDeleted || !user.username ? localize('profile.my.blocked.deletedAccount') : `@${user.username}`;

const unBlockUser = async (id: string, name: string) => {
  if (!window.confirm(`Are you sure you want to unblock ${name}?`)) return;

  await gambits.userService.toggleBlock(id, true);
  blockedUsers.value = blockedUsers.value.filter((el) => el.id !== id);
  await authStore.fetchUserInfo(true);
};
</script>

<style lang="scss" scoped>
.info-blocked {
  line-height: 1.6;
  color: var(--color-white-70);
}

.current-blocked {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: stretch;
  width: 100%;
  gap: 1rem;
  color: var(--color-white-70);
}

.blocked-user-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 16px;

  .rating {
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
